import './ApplicationCentric.scss';

import { PortfolioService, TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { ApplicationCost, DatabaseCost, ServerCost } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Alert, Box, Header, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../common/Constants';
import { constants } from '../../common/Localization';
import { formatCurrencyNoDecimals } from '../../common/LocalizationHelper';
import AWSCATTable from '../../common/table/Table-index';
import { MatchParam } from '../../common/types';
import { HelpPanelContext } from '../../contexts/HelpPanelContext';
import { InfoLink } from '../common/InfoLink';
import PreviewBanner from '../common/PreviewBanner';
import { getDatabaseCostColumnDefinition, getServerCostColumnDefinition } from './ApplicationCostColumnDefinitions';

const portfolioService = new PortfolioService();
const tcoService = new TcoService();
type Props = FlashContextInterface;

function ApplicationCostDetailPage({ flash }: Props): JSX.Element {

    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/management/application-detail/:applicationId',
    });
    const portfolioId = match?.params?.portfolioId || '';
    const applicationId = match?.params?.applicationId || '';
    const { panelHandler } = useContext(HelpPanelContext);
    const isMounted = useIsMounted();

    const [ loading, setLoading ] = useState(false);
    const [ applicationCost, setApplicationCost ] = useState<ApplicationCost | null>(null);
    const [ currency, setCurrency ] = useState<string>('');
    
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                const portfolio = await portfolioService.get(portfolioId);
                const assessmentId = portfolio.defaultTcoAssessmentId;
                if (assessmentId) {
                    const { appCost, currency } = await tcoService.getApplicationCentricCost(assessmentId, applicationId);
                    if (isMounted()) {
                        setApplicationCost(appCost);
                        setCurrency(currency);
                    }
                }
            } catch (err) {
                if (isMounted()) {
                    flash.alert(FlashType.error, (err as Error).message, constants.FAILED_TO_LOAD_APP_COST_DETAIL);
                }
            } finally {
                if (isMounted()) {
                    setLoading(false);
                }
            }
        };
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ applicationId, portfolioId ]);

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.APPLICATION_COST_DETAILS);
    }, [ panelHandler ]);

    const header = (
        <Header variant='h1'>
            <SpaceBetween direction='horizontal' size='xs'>
                <Box variant='h1' display='inline'>{constants.COST_DETAILS}</Box>
                <InfoLink className='data-quality-info-link' topic={HelpPanelTopicOptions.APPLICATION_COST_DETAILS} />
            </SpaceBetween>
        </Header>
    );

    const awsTotalCost = applicationCost ? formatCurrencyNoDecimals(
        applicationCost.aws.serverCosts.totalServerCosts + applicationCost.aws.databaseCosts.totalDatabaseCosts, currency
    ) : 0;

    const onPremTotalCost = applicationCost ? formatCurrencyNoDecimals(
        applicationCost.onPremises.serverCosts.totalServerCosts + applicationCost.onPremises.databaseCosts.totalDatabaseCosts,
        currency
    ) : 0;

    const onPremCommonServerCost = applicationCost ? formatCurrencyNoDecimals(
        applicationCost.onPremises.commonCosts.serverFacilities + applicationCost.onPremises.commonCosts.serverHardware +
            applicationCost.onPremises.commonCosts.serverMaintenance, currency
    ) : 0;

    const onPremCommonDBCost = applicationCost ? formatCurrencyNoDecimals(
        applicationCost.onPremises.commonCosts.databaseFacilities + applicationCost.onPremises.commonCosts.databaseHardware +
            applicationCost.onPremises.commonCosts.databaseMaintenance, currency
    ) : 0;

    const onPremCommonOtherCost = applicationCost ? formatCurrencyNoDecimals(
        applicationCost.onPremises.commonCosts.labor + applicationCost.onPremises.commonCosts.network +
                applicationCost.onPremises.commonCosts.storage + applicationCost.onPremises.commonCosts.storageBackup +
                applicationCost.onPremises.commonCosts.storageRack + applicationCost.onPremises.commonCosts.storageSoftware +
                applicationCost.onPremises.commonCosts.manualAdjustment, currency
    ) : 0;

    const costTable = applicationCost ? (
        <Box className='scroll-horizontal'>
            <table className='awsui data-table white-background'>
                <thead>
                    <tr>
                        <th/><th/><th/>
                        <th>
                            <div className='awsui-table-header-content'>{constants.SERVER_COST_CAPITALIZED}</div>
                        </th>
                        <th>
                            <div className='awsui-table-header-content'>{constants.DATABASE_COST_CAPITALIZED}</div>
                        </th>
                        <th>
                            <div className='awsui-table-header-content'>{constants.OTHER_COST_CAPITALIZED}</div>
                        </th>
                        <th>
                            <div className='awsui-table-header-content'>{constants.TOTAL_COSTS_CAPITALIZED}</div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td rowSpan={12}>
                            <strong>{constants.AWS}</strong>
                        </td>
                        <td rowSpan={6}>
                            <strong>{constants.DIRECT_CAPITALIZED}</strong>
                        </td>
                        <td>{constants.COMPUTE_COST}</td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.aws.serverCosts.computeCost, currency)}</Box></td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.aws.databaseCosts.computeCost, currency)}</Box></td>
                        <td/>
                        <td rowSpan={4}/>
                    </tr>
                    <tr>
                        <td>{constants.OS_LICENSE}</td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.aws.serverCosts.osLicenseCost ?? 0, currency)}</Box></td>
                        <td><Box textAlign='right'>-</Box></td>
                        <td/>
                    </tr>
                    <tr>
                        <td>{constants.DATABASE_LICENSE}</td>
                        <td><Box textAlign='right'>-</Box></td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.aws.databaseCosts.licenseCost, currency)}</Box></td>
                        <td/>
                    </tr>
                    <tr>
                        <td>{constants.STORAGE_COST}</td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.aws.serverCosts.storageCost, currency)}</Box></td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.aws.databaseCosts.storageCost, currency)}</Box></td>
                        <td/>
                    </tr>
                    <tr>
                        <td>{constants.RI_DISCOUNT}</td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.aws.serverCosts.riDiscount ?? 0, currency)}</Box></td>
                        <td><Box textAlign='right'>-</Box></td>
                        <td/>
                    </tr>
                    <tr>
                        <td>{constants.TOTAL_DIRECT_COST}</td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.aws.serverCosts.totalServerCosts, currency)}</Box></td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.aws.databaseCosts.totalDatabaseCosts, currency)}</Box></td>
                        <td/>
                        <td><Box textAlign='right'>{awsTotalCost}</Box></td>
                    </tr>
                    <tr>
                        <td rowSpan={6}>
                            <strong>{constants.COMMON}</strong>
                        </td>
                        <td>{constants.LABOR_COST}</td>
                        <td/>
                        <td/>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.aws.commonCosts.labor, currency)}</Box></td>
                        <td rowSpan={5}/>
                    </tr>
                    <tr>
                        <td>{constants.NETWORK_COST}</td>
                        <td/>
                        <td/>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.aws.commonCosts.network, currency)}</Box></td>
                    </tr>
                    <tr>
                        <td>{constants.STORAGE_COST}</td>
                        <td/>
                        <td/>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.aws.commonCosts.storage, currency)}</Box></td>
                    </tr>
                    <tr>
                        <td>{constants.SUPPORT_COST}</td>
                        <td/>
                        <td/>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.aws.commonCosts.support, currency)}</Box></td>
                    </tr>
                    <tr>
                        <td>{constants.ADJUSTMENT}</td>
                        <td/>
                        <td/>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.aws.commonCosts.manualAdjustment, currency)}</Box></td>
                    </tr>
                    <tr>
                        <td>{constants.TOTAL_COMMON_COST}</td>
                        <td/>
                        <td/>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.aws.commonCosts.totalCommonCost, currency)}</Box></td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.aws.commonCosts.totalCommonCost, currency)}</Box></td>
                    </tr>
                    <tr>
                        <td rowSpan={16}>
                            <strong>{constants.ON_PREMISES}</strong>
                        </td>
                        <td rowSpan={5}>
                            <strong>{constants.DIRECT_CAPITALIZED}</strong>
                        </td>
                        <td>{constants.COMPUTE_COST}</td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.serverCosts.computeCost, currency)}</Box></td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.databaseCosts.computeCost, currency)}</Box></td>
                        <td/>
                        <td rowSpan={4}/>
                    </tr>
                    <tr>
                        <td>{constants.LICENSE_COST}</td>
                        <td><Box textAlign='right'>-</Box></td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.databaseCosts.licenseCost, currency)}</Box></td>
                        <td/>
                    </tr>
                    <tr>
                        <td>{constants.SOFTWARE_COST}</td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.serverCosts.softwareCost, currency)}</Box></td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.databaseCosts.softwareCost, currency)}</Box></td>
                        <td/>
                    </tr>
                    <tr>
                        <td>{constants.STORAGE_COST}</td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.serverCosts.storageCost, currency)}</Box></td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.databaseCosts.storageCost, currency)}</Box></td>
                        <td/>
                    </tr>
                    <tr>
                        <td>{constants.TOTAL_DIRECT_COST}</td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.serverCosts.totalServerCosts, currency)}</Box></td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.databaseCosts.totalDatabaseCosts, currency)}</Box></td>
                        <td/>
                        <td><Box textAlign='right'>{onPremTotalCost}</Box></td>
                    </tr>
                    <tr>
                        <td rowSpan={11}>
                            <strong>{constants.COMMON}</strong>
                        </td>
                        <td>{constants.FACILITIES}</td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.commonCosts.serverFacilities, currency)}</Box></td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.commonCosts.databaseFacilities, currency)}</Box></td>
                        <td/>
                        <td rowSpan={10}/>
                    </tr>
                    <tr>
                        <td>{constants.HARDWARE}</td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.commonCosts.serverHardware, currency)}</Box></td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.commonCosts.databaseHardware, currency)}</Box></td>
                        <td/>
                    </tr>
                    <tr>
                        <td>{constants.MAINTENANCE}</td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.commonCosts.serverMaintenance, currency)}</Box></td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.commonCosts.databaseMaintenance, currency)}</Box></td>
                        <td/>
                    </tr>
                    <tr>
                        <td>{constants.LABOR}</td>
                        <td/>
                        <td/>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.commonCosts.labor, currency)}</Box></td>
                    </tr>
                    <tr>
                        <td>{constants.ASSETS_NETWORK}</td>
                        <td/>
                        <td/>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.commonCosts.network, currency)}</Box></td>
                    </tr>
                    <tr>
                        <td>{constants.STORAGE_CAPITALIZED}</td>
                        <td/>
                        <td/>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.commonCosts.storage, currency)}</Box></td>
                    </tr>
                    <tr>
                        <td>{constants.STORAGE_BACKUP}</td>
                        <td/>
                        <td/>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.commonCosts.storageBackup, currency)}</Box></td>
                    </tr>
                    <tr>
                        <td>{constants.STORAGE_RACK}</td>
                        <td/>
                        <td/>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.commonCosts.storageRack, currency)}</Box></td>
                    </tr>
                    <tr>
                        <td>{constants.STORAGE_SOFTWARE}</td>
                        <td/>
                        <td/>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.commonCosts.storageSoftware, currency)}</Box></td>
                    </tr>
                    <tr>
                        <td>{constants.ADJUSTMENT}</td>
                        <td/>
                        <td/>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.commonCosts.manualAdjustment, currency)}</Box></td>
                    </tr>
                    <tr>
                        <td>{constants.TOTAL_COMMON_COST}</td>
                        <td><Box textAlign='right'>{onPremCommonServerCost}</Box></td>
                        <td><Box textAlign='right'>{onPremCommonDBCost}</Box></td>
                        <td><Box textAlign='right'>{onPremCommonOtherCost}</Box></td>
                        <td><Box textAlign='right'>{formatCurrencyNoDecimals(applicationCost.onPremises.commonCosts.totalCommonCost, currency)}</Box></td>
                    </tr>
                </tbody>
            </table>
        </Box>
    ) : null;

    const serverTable = applicationCost ? (
        <AWSCATTable<ServerCost>
            columnDefinitions={getServerCostColumnDefinition(currency)}
            items={applicationCost.servers}
            resourceName={constants.SERVERS}
        />
    ) : null;

    const dbTable = applicationCost ? (
        <AWSCATTable<DatabaseCost>
            columnDefinitions={getDatabaseCostColumnDefinition(currency)}
            items={applicationCost.databases}
            resourceName={constants.DATABASES}
        />
    ) : null;

    const noApplicationCostAlert = applicationCost ? null : (
        <Alert
            header={constants.APPLICATION_COST_DETAIL_HAS_NOT_BEEN_COMPUTED}
        >
            {constants.RETURN_TO_TCO_AND_CALCULATE}
        </Alert>
    );

    const content = (
        <SpaceBetween size='m'
            data-testid='content'
        >
            {noApplicationCostAlert}
            {costTable}
            {serverTable}
            {dbTable}
        </SpaceBetween>
    );

    return (
        <SpaceBetween size='m'>
            <PreviewBanner />
            {header}
            {loading ? <Spinner size='big' /> : content}
        </SpaceBetween>
    );
}

export default withFlashContext(ApplicationCostDetailPage);
